import { Link } from 'react-router';
import type { Route } from './+types/blog';
import { buildMeta } from '~/lib/seo';
import { brandingFromMatches, useBranding } from '~/lib/branding';
import { posts, categories } from '~/lib/blog';

/**
 * The blog index.
 *
 * This replaces a reverse proxy to a WordPress install on Pantheon. See
 * `src/app/lib/blog.ts` for why, and `content/CONTENT-PLAN.md` for what goes in it.
 *
 * There is no pagination and that is on purpose. The old blog had `/blog/page/2/` and
 * `/blog/page/3/` in its markup with almost nothing behind them. Paginated archives split
 * link equity across pages nobody links to, and at the volume a residential cleaning
 * company should publish (see PUBLISHING-PLAN.md: weekly, not daily) a single list is
 * still short enough to read. If this ever passes a hundred posts, filter by category
 * before reaching for pages.
 */
export const meta: Route.MetaFunction = ({ matches }) => {
  const branding = brandingFromMatches(matches);
  return buildMeta({
    title: 'Cleaning, explained',
    description:
      'How we clean, what we use, and how we run the company. Written by the people who do the work, for homeowners in Tampa Bay.',
    path: '/blog',
    siteName: branding.name,
  });
};

const CATEGORY_LABELS: Record<string, string> = {
  why: 'Why it matters',
  how: 'How we work',
  products: 'What we use',
  us: 'Who we are',
  hiring: 'Hiring a cleaner',
};

function formatDate(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/New_York',
  });
}

export default function BlogIndex() {
  const branding = useBranding();
  const cats = categories();

  return (
    <main className="blog-index">
      <header className="blog-index-head">
        <h1>Cleaning, explained</h1>
        <p className="blog-index-lede">
          How we clean, what we use, and how {branding.name} is run. Written by the people
          doing the work.
        </p>
      </header>

      {posts.length === 0 ? (
        <p className="blog-empty">The first posts are being written. Check back shortly.</p>
      ) : (
        cats.map((cat) => {
          const inCat = posts.filter((p) => p.category === cat);
          if (inCat.length === 0) return null;
          return (
            <section key={cat} className="blog-group">
              <h2 className="blog-group-title">{CATEGORY_LABELS[cat] ?? cat}</h2>
              <ul className="blog-list">
                {inCat.map((post) => (
                  <li key={post.slug} className="blog-card">
                    <h3>
                      <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                    </h3>
                    <p className="blog-card-desc">{post.description}</p>
                    <p className="blog-card-meta">
                      <time dateTime={post.date}>{formatDate(post.date)}</time>
                      <span aria-hidden="true"> · </span>
                      {post.readingMinutes} min read
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          );
        })
      )}
    </main>
  );
}
